import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "../lib/brand";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/chi-sono", label: "Chi Sono" },
  { to: "/metodo-tata", label: "Metodo T.A.T.A." },
  { to: "/cosa-posso-fare-per-te", label: "Cosa posso fare per te" },
  { to: "/risorse", label: "Biblioteca T.A.T.A." },
  { to: "/dicono-di-me", label: "Dicono di me" },
  { to: "/podcast", label: "Podcast" },
  { to: "/magazine", label: "Magazine" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-white/75 border-b border-[#81C3C9]/25" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img
              src={BRAND.logo}
              alt="Teresa Vilardo Logo"
              className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover transition-transform duration-500 group-hover:rotate-[8deg]"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-2xl md:text-3xl text-[#3D2140]">Teresa Vilardo</span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#FF2768] font-semibold">Family Coach Montessori</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="main-nav">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "") || "home"}`}
                className={`link-underline text-sm font-medium uppercase tracking-wider transition-colors ${
                  location.pathname === item.to ? "text-[#FF2768]" : "text-[#3D2140] hover:text-[#FF2768]"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/prenota" data-testid="header-cta-booking" className="btn-tv-primary text-sm">
              Prenota consulenza
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 rounded-full border border-[#3D2140]/20"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            data-testid="mobile-menu"
            className="fixed top-[80px] left-0 right-0 z-40 md:hidden bg-white border-b border-[#81C3C9]/30 shadow-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                  className="text-lg font-medium text-[#3D2140]"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
